import { API_BASE_URL } from "../api/api";
import React, { useState, useEffect } from "react";
import { Form, Input, Button, message } from "antd";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";

const ForgotPassword = () => {
  const [form] = Form.useForm();
  const [step, setStep] = useState(1); // 1: enter email, 2: enter code
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();

  // Start countdown
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const sendCode = async (targetEmail) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}forgetPassword`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: targetEmail }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        message.success(data.message || "Verification code sent.");
        setEmail(targetEmail);
        setStep(2);
        setCountdown(60); // Start countdown after successful send
      } else {
        message.error(data.error || "Failed to send verification code.");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (values) => {
    if (!values.code) {
      message.warning("Please enter the verification code.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}verifyCode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: values.code }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        message.success("Code verified.");
        navigate(
          `/reset-password?email=${encodeURIComponent(
            email
          )}&code=${encodeURIComponent(values.code)}`
        );
      } else {
        message.error(data.error || "Invalid code.");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = (values) => {
    if (step === 1) {
      sendCode(values.email);
    } else {
      verifyCode(values);
    }
  };

  const handleResend = () => {
    if (email) {
      sendCode(email);
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(135deg, #eef2f3, #8e9eab)",
        padding: 20,
      }}
    >
      <div
        style={{
          width: 420,
          padding: "48px 36px",
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 14px 40px rgba(0,0,0,0.1)",
          textAlign: "center",
        }}
      >
        <img
          src={logo}
          alt="CRM Logo"
          style={{ width: 90, marginBottom: 20 }}
        />

        <h2 style={{ marginBottom: 6, fontWeight: 600, fontSize: 24 }}>
          {step === 1 ? "Reset Your Password" : "Enter Verification Code"}
        </h2>
        <p style={{ marginBottom: 28, color: "#666" }}>
          {step === 1
            ? "Enter your email to receive a reset code"
            : "Check your email and enter the 6-digit code"}
        </p>

        <Form layout="vertical" onFinish={onFinish} form={form}>
          {step === 1 && (
            <Form.Item
              label="Email Address"
              name="email"
              rules={[{ required: true, message: "Please enter your email" }]}
            >
              <Input placeholder="you@company.com" />
            </Form.Item>
          )}

          {step === 2 && (
            <>
              <Form.Item
                label="Verification Code"
                name="code"
                rules={[{ required: true, message: "Please enter the code" }]}
              >
                <Input placeholder="6-digit code" maxLength={6} />
              </Form.Item>

              <div style={{ marginBottom: 16 }}>
                {countdown > 0 ? (
                  <span style={{ fontSize: 13, color: "#999" }}>
                    Resend code in {countdown}s
                  </span>
                ) : (
                  <Button type="link" onClick={handleResend}>
                    Resend Code
                  </Button>
                )}
              </div>
            </>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {step === 1 ? "Send Code" : "Continue"}
            </Button>
          </Form.Item>
        </Form>

        <div style={{ marginTop: 16 }}>
          <Link to="/" style={{ fontSize: 12, color: "#888" }}>
            Back to Login
          </Link>
        </div>

        <div style={{ fontSize: 12, color: "#aaa", marginTop: 30 }}>
          © {new Date().getFullYear()} Sunbook.ca · All rights reserved
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
